/* eslint-disable */
/*
 * Redux module for handling lexicon customization.
 */

/*
 * This is an example of what the state looks like.
 *

const sampleState = {
  lexicons: {
    'b2a0000d-abad-4d19-b724-ebf15845dfaa': {
      name: 'Company Names'
    },
    '8f96e053-910b-43e4-b792-222edeb1f9ad': {
      name: 'Surnames'
    }
  },
  currentLexicon: 'b2a0000d-abad-4d19-b724-ebf15845dfaa',
  lexentries: {
    'lexentry-uuid-123': {
      graphemes: 'xfinity',
      soundslike: 'x finity',
      phonemes: 'EH K S F IH N IH T IY',
      unigram: null,
      prob: null,
      silprobs: null },
    'lexentry-uuid-456': {
      graphemes: 'xfinity',
      soundslike: null,
      phonemes: 'EH K S F IY N IY T AY',
      unigram: null,
      prob: null,
      silprobs: null },
    'lexentry-uuid-789': {
      graphemes: 'remeeting',
      soundslike: null,
      phonemes: 'R IY M IY T IH NG',
      unigram: -5.0,
      prob: 1.0,
      silprobs: '0.3 1.0 1.0' }
  }
}
*/

const initialState = {
  lexicons: { },
  currentLexicon: null,
  lexentries: {
  }
}

/*
 * Since the logic for updating the lexentry fields is identical, we
 * only have one reducer/action (updateField, UPDATE_FIELD) for
 * updating all the fields.
 * 
 * There's probably a cleaner way to do the immutable copies.
 *
 * This should probably be broken up into slices -- it's getting a bit big.
 */

export default (state = initialState, action) => {
  let newentries
  let newentry
  let newlexicons

  switch (action.type) {

    case 'UPDATE_FIELD':
      newentries = {...state.lexentries}
      newentry = {...newentries[action.lexentry_uuid]}
      newentry[action.field] = action.value
      newentries[action.lexentry_uuid] = newentry
      return {...state, lexentries: newentries}

    case 'ADD_LEXICON':
      newlexicons = {...state.lexicons}
      newlexicons[action.lexicon_uuid] = { name: action.name }
      return {...state, currentLexicon: action.lexicon_uuid, lexicons: newlexicons, lexentries: {} }

    case 'ADD_LEXENTRY':
      newentries = {...state.lexentries}
      newentries[action.lexentry_uuid] = { graphemes: action.graphemes, soundslike: action.soundslike, phonemes: action.phonemes, unigram: action.unigram, prob: action.prob, silprobs: action.silprobs }
      return {...state, lexentries: newentries}

    case 'DELETE_LEXENTRY':
      newentries = {...state.lexentries}
      // Lint thinks the ", res" is extraneous. It isn't because it's what the expr returns.
      // eslint-disable-next-line
      return {...state, lexentries: Object.keys(newentries).filter(uuid => uuid !== action.lexentry_uuid).reduce((res, key) => (res[key] = newentries[key], res), {})}

    case 'LEXICONS_RECEIVED':
      newlexicons = {}
      for (let lex of action.lexicons) {
	newlexicons[lex.lexicon_uuid] = { name: lex.name }
      }
      return {...state,
	      currentLexicon: action.lexicons.length === 0 ? null : action.lexicons[0].lexicon_uuid,
	      lexicons: newlexicons }

    case 'LEXENTRIES_RECEIVED':
      newentries = {}
      for (let entry of action.lexentries) {
	newentries[entry.lexentry_uuid] = entry
      }
      return {...state, lexentries: newentries, currentLexicon: action.lexicon_uuid}

    case 'CLEAR_LEXENTRIES':
      return {...state, lexentries: {}, currentLexicon: null}

    default:
      return state
  }
}

/*
 * Update the value of a single lexentry field (e.g. field is one of
 * 'graphemes, 'phonemes', etc). Always updates locally. If sendToApi
 * is true, also sends asynchronously to the API.
 */

export const updateField = (lexicon_uuid, lexentry_uuid, field, value, sendToApi) => {
  return function(dispatch, getState) {
    dispatch({
      type: 'UPDATE_FIELD',
      lexentry_uuid: lexentry_uuid,
      field: field,
      value: value,
    })

    if (sendToApi) {
      const rmi = getState().rmi
      let config = { lexicon_uuid: lexicon_uuid, lexentry_uuid: lexentry_uuid }
      config[field] = value
      rmi.lexentry.put(config).then().catch(m => {
	console.log("Error in updateField")
	console.log(m)
      })
    }
  }  
}

/*
 * Create a new lexicon. Note that the API creates the lexicon_uuid.
 */

export const addLexicon = (name) => {
  return function(dispatch, getState) {
    const rmi = getState().rmi
    rmi.lexicons.post({name: name}).then(lex => {
      dispatch(lexiconReceived(lex))
    }).catch(m => {
      console.log("Error in addLexicon")
      console.log(m)
    })
  }
}

/*
 * Got a lexicon from the API
 */

export const lexiconReceived = (lex) => {
  return {
    type: 'ADD_LEXICON',
    lexicon_uuid: lex.lexicon_uuid,
    name: lex.name,
  }
}

/*
 * Add a new lexicon entry. Only the lexicon_uuid is required. Note
 * that the API call creates the lexentry_uuid.
 */

export const addLexentry = (lexicon_uuid, graphemes='', soundslike='', phonemes='', unigram='', prob='', silprobs='') => {
  return function(dispatch, getState) {
    const rmi = getState().rmi
    rmi.lexentry.post({ lexicon_uuid: lexicon_uuid, graphemes: graphemes, soundslike: soundslike, phonemes: phonemes, unigram: unigram, prob: prob}).then(le => {
      dispatch(lexentryReceived(le))
    }).catch(m => {
      console.log("Error in addLexentry")
      console.log(m)
    })
  }
}

/*
 * Got a lexentry from the API
 */

export const lexentryReceived = (le) => {
  return {
    type: 'ADD_LEXENTRY',
    lexentry_uuid: le.lexentry_uuid,
    graphemes: le.graphemes,
    soundslike: le.soundslike,
    phonemes: le.phonemes,
    unigram: le.unigram,
    prob: le.prob,
    silprobs: le.silprobs,
  }
}

/*
 * Delete a lexicon.
 *
 * Note that this doesn't directly change state. Instead, it sends a request
 * to the API to delete the lexicon, and then reloads the lexicons from
 * the API. This is a bit inefficient, but deleting lexicons is uncommon.
 */

export const deleteLexicon = (lexicon_uuid) => {
  return function(dispatch, getState) {
    const rmi = getState().rmi
    rmi.lexicons.delete({lexicon_uuid: lexicon_uuid}).then(m => {
      dispatch(getLexicons())
    }).catch(m => {
      console.log("Error in deleteLexentry")
      console.log(m)
    })
  }
}

/*
 * Rename a lexicon.
 *
 * Note that this doesn't directly change state. Instead, it sends a request
 * to the API to rename the lexicon, and then reloads the lexicons from
 * the API.  This is a bit inefficient, but renaming lexicons is uncommon.
 */

export const renameLexicon = (lexicon_uuid, newname) => {
  return function(dispatch, getState) {
    const rmi = getState().rmi
    rmi.lexicons.put({lexicon_uuid: lexicon_uuid, name: newname}).then(m => {
      dispatch(getLexicons());
    }).catch(m => {
      console.log("Error in renameLexentry")
      console.log(m)
    })
  }
}

/*
 * Delete a lexical entry locally and asynchronously to the API.
 */

export const deleteLexentry = (lexicon_uuid, lexentry_uuid) => {
  return function(dispatch, getState) {
    dispatch({
      type: 'DELETE_LEXENTRY',
      lexentry_uuid: lexentry_uuid
    })
    const rmi = getState().rmi
    rmi.lexentry.delete({lexicon_uuid: lexicon_uuid, lexentry_uuid: lexentry_uuid}).then().catch(m => {
      console.log("Error in deleteLexentry")
      console.log(m)
    })
  }
}

/*
 * Generate phonemes from the "soundslike" entry, and create new lexentries using 
 * the given word for "graphemes". First call the API to generate the phonemes,
 * then dispatch a new lexical entry for each returned phoneme string.
 */

export const addG2P = (lexicon_uuid, word, soundslike, language) => {
  if (!word || !soundslike) {
    /* Do nothing if no word or no soundslike */
    return function(dispatch) { }
  }
  
  /* I'm not sure how much html unescaping I should do here, but
   * this much at least...
   */
  soundslike = soundslike.replace(/&nbsp;/g, ' ').replace(/ +/g, ' ')

  if (! /\S/.test(soundslike)) {
    /* If there isn't at least one non-space, do nothing. */
    return function(dispatch) { }
  }

  soundslike = soundslike.replace(/ /g, '_')

  return function(dispatch, getState) {
    const rmi = getState().rmi
    return rmi.lexicons.getPhonemes(soundslike, {language}).then(prons => {
      /* Multiple dispatches are inefficient, but there should only be a few */
      for (let item of prons.items) {
	let les = getState().customizelexicon.lexentries
	let les_keys = Object.keys(les)
	let isdup = false
	for (let le of les_keys) {
	  if (le.graphemes === word && le.phonemes === item) {
	    isdup = true
	    break
	  }
	}
	if (! isdup) {
	  dispatch(addLexentry(lexicon_uuid, word, '', item))
	}
      }
    }).catch(m => {
      console.log('Error in g2p')
      console.log(m)
    })
  }
}

export const getLexicons = () => {
  return function(dispatch, getState) {
    const rmi = getState().rmi
    return rmi.lexicons.list().then(lexs => {
      dispatch(lexiconsReceived(lexs))
      if (lexs.length > 0) {
	dispatch(getLexentries(lexs[0].lexicon_uuid))
      } else {
	dispatch(clearLexentries())
      }
    }).catch(m => {
      console.log('Error in getLexicons')
      console.log(m)
    })
  }
}

export const clearLexentries = () => {
  return {
    type: 'CLEAR_LEXENTRIES',
  }
}

export const lexiconsReceived = (lexs) => {
  return {
    type: 'LEXICONS_RECEIVED',
    lexicons: lexs,
  }
}      

export const getLexentries = (lexicon_uuid) => {
  return function(dispatch, getState) {
    const rmi = getState().rmi
    return rmi.lexentry.list({lexicon_uuid: lexicon_uuid}).then(les => {
      dispatch(lexentriesReceived(lexicon_uuid, les))
    }).catch(m => {
      console.log('Error in getLexentries')
      console.log(m)
    })
  }
}

export const lexentriesReceived = (lexicon_uuid, les) => {
  return {
    type: 'LEXENTRIES_RECEIVED',
    lexentries: les,
    lexicon_uuid: lexicon_uuid
  }
}
